import React, { MutableRefObject, useState } from "react";
import { ChildrenProps } from "../models/componentModel";
import {
    popupListObj,
    functionParameterTypeList,
    functionMethodList,
    timezoneList,
    routeSourceObj,
    feedbackStatus,
    feedbackKnowledgeBaseStatus,
    breadCrumbObj,
    gptModelConfig,
    CommonResponseModel,
    gptModels,
    voiceAgentModels,
    voiceModelConfig,
    AILLMPlatformType,
} from "../models/axiosModel";
import agent from "../services/api";
import { homeBreadcrumb } from "../utils/breadcrumb";

const GenericDataContext = React.createContext<GenericDataContextPayload | undefined>(
    undefined
);

export interface GenericDataContextPayload {
    popupList: popupListObj[];
    setPopupList: React.Dispatch<React.SetStateAction<popupListObj[]>>
    GetPopupList: () => void
    getFunctionParamsList: () => void
    functionParameterTypeDataList: functionParameterTypeList[]
    functionMethodsList: functionMethodList[]
    getFunctioMethodList: () => void;
    genericTimezoneList: timezoneList[];
    setGenericTimezoneList: React.Dispatch<React.SetStateAction<timezoneList[]>>
    getGenericTimezoneList: () => void;
    routeSources: routeSourceObj[];
    setRouteSources: React.Dispatch<React.SetStateAction<routeSourceObj[] | []>>;
    GetRouteSourceList: () => void;
    feedbackStatusData: feedbackStatus[],
    setFeedbackStatusData: React.Dispatch<React.SetStateAction<feedbackStatus[]>>,
    feedbackKnowledgeBaseStatusData: feedbackKnowledgeBaseStatus[],
    setFeedbackKnowledgeBaseStatusData: React.Dispatch<React.SetStateAction<feedbackKnowledgeBaseStatus[]>>,
    breadcrumbs: breadCrumbObj[],
    setBreadCrumbs: React.Dispatch<React.SetStateAction<breadCrumbObj[]>>,
    gptModelList: gptModels[],
    setGptModelList: React.Dispatch<React.SetStateAction<gptModels[]>>,
    voiceAgentsModelList: voiceAgentModels[],
    setVoiceAgentsModelList: React.Dispatch<React.SetStateAction<voiceAgentModels[]>>,
    getGptModelList: () => void;
    getVoiceAgentsModelList: () => void;
    AILLMPlatformList: AILLMPlatformType[] | null, 
    setAILLMPlatformList: React.Dispatch<React.SetStateAction<AILLMPlatformType[] | null>>,
    getAILLMPlatformApi: () => Promise<CommonResponseModel<AILLMPlatformType>>
}

const GenericDataProvider = ({ children }: ChildrenProps) => {

    const [popupList, setPopupList] = useState<popupListObj[]>([])
    const GetPopupList = async () => {
        const response = await agent.getPopupList.get();
        if (response.status) {
            setPopupList(response.data)
        } else {
            setPopupList([])
        }
    }
    const [functionParameterTypeDataList, setFunctionParameterTypeDataList] = useState([])
    const [functionMethodsList, setFunctionMethodsList] = useState([]);
    const [genericTimezoneList, setGenericTimezoneList] = useState<timezoneList[]>([])
    const [routeSources, setRouteSources] = useState<routeSourceObj[] | []>([])
    const [breadcrumbs, setBreadCrumbs] = useState<breadCrumbObj[]>([])
    const [feedbackStatusData, setFeedbackStatusData] = useState<feedbackStatus[]>([]);
    const [feedbackKnowledgeBaseStatusData, setFeedbackKnowledgeBaseStatusData] = useState<feedbackKnowledgeBaseStatus[]>([]);
    const [gptModelList, setGptModelList] = useState<gptModels[]>([])
    const [voiceAgentsModelList, setVoiceAgentsModelList] = useState<voiceAgentModels[]>([])
    const [AILLMPlatformList, setAILLMPlatformList] = useState<AILLMPlatformType[] | null>(null)

    const getFunctionParamsList = async () => {
        const response = await agent.getFunctionParameterTypeData.get();
        if (response.status) {
            setFunctionParameterTypeDataList(response.data)
        } else {
            setFunctionParameterTypeDataList([])
        }
    }
    const getFunctioMethodList = async () => {
        const response = await agent.getFunctionMethods.get();
        if (response.status) {
            setFunctionMethodsList(response.data)
        } else {
            setFunctionMethodsList([])
        }
    }
    const getGenericTimezoneList = async () => {
        const response = await agent.getGenericTimezone.get();
        if (response.status) {
            setGenericTimezoneList(response.data)
        } else {
            setGenericTimezoneList([])
        }
    }
    const GetRouteSourceList = async () => {
        const response = await agent.getRouteSourceIds.get();
        if (response.status) {
            setRouteSources(response.data)
        } else {
            setRouteSources([])
        }
    }
    const getGptModelList = async () => {
        const response = await agent.getGptModelList.get();
        if (response.status && response.data) {
            let gptModelList = response.data.map((gptModel: gptModelConfig) => {
                return {
                    gpt_model_id: gptModel.model_id.toString(),
                    gpt_model_name: gptModel.model_name,
                    ai_llm_platform_id: gptModel.ai_llm_platform_id,
                }
            })
            setGptModelList(gptModelList)
        }
    }
    const getVoiceAgentsModelList = async () => {
        const response = await agent.getVoiceAgentsModelList.get();
        if (response.status && response.data) {
            let voiceAgentsModelList = response.data.map((voiceModel: voiceModelConfig) => {
                return {
                    voice_agent_model_id: voiceModel.voice_agent_model_id,
                    voice_agent_model_name: voiceModel.voice_agent_model_name
                }
            })
            setVoiceAgentsModelList(voiceAgentsModelList)
        }
    }

    const getAILLMPlatformApi = async () => {
        const res = await agent.getAILLMPlatform.get();
        if(res?.status){
            setAILLMPlatformList(res?.data);
        } else {
            setAILLMPlatformList(null);
        }
        return res;
    }

    const payload = {
        popupList,
        setPopupList,
        GetPopupList,
        getFunctionParamsList,
        functionParameterTypeDataList,
        functionMethodsList,
        getFunctioMethodList,
        genericTimezoneList,
        setGenericTimezoneList,
        getGenericTimezoneList,
        routeSources,
        setRouteSources,
        GetRouteSourceList,
        feedbackStatusData,
        setFeedbackStatusData,
        feedbackKnowledgeBaseStatusData,
        setFeedbackKnowledgeBaseStatusData,
        breadcrumbs,
        setBreadCrumbs,
        gptModelList,
        setGptModelList,
        voiceAgentsModelList,
        setVoiceAgentsModelList,
        getGptModelList,
        getVoiceAgentsModelList,
        AILLMPlatformList, 
        setAILLMPlatformList,
        getAILLMPlatformApi
    };
    return <GenericDataContext.Provider value={payload}>{children}</GenericDataContext.Provider>;
};

export { GenericDataContext, GenericDataProvider };
